import { UserRoundX } from '@/lib/icons';
import { ReportPageLayout } from '@/components/reports';
import { reportsService } from '@/services/reports-service';
import type { ColumnDef, FilterConfig, ReportFilters } from '@/types/reports';
import { formatDisplayDateTime, formatDisplayDate } from '@/lib/date-utils';

interface LoginEvent {
  id: string;
  attributes: {
    affiliate_name: string;
    user_info: {
      first_name: string;
      last_name: string;
      email: string;
      three_day_certified_date?: string;
    };
    login_date: string;
  };
}

const columns: ColumnDef<LoginEvent>[] = [
  {
    key: 'user',
    header: 'User',
    render: (item) => (
      <div>
        <div className="font-medium">
          {item.attributes.user_info.first_name} {item.attributes.user_info.last_name}
        </div>
        <div className="text-muted-foreground text-xs">
          {item.attributes.user_info.email}
        </div>
        {item.attributes.affiliate_name && (
          <div className="text-muted-foreground text-xs mt-0.5">
            {item.attributes.affiliate_name}
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'three_day_certified',
    header: '3-Day Certified',
    render: (item) =>
      item.attributes.user_info.three_day_certified_date
        ? formatDisplayDate(item.attributes.user_info.three_day_certified_date)
        : '-',
  },
  {
    key: 'login_date',
    header: 'Login Date',
    render: (item) => formatDisplayDateTime(item.attributes.login_date),
  },
];

const filterConfig: FilterConfig = {
  fields: [
    { key: 'email_domain', type: 'text', label: 'Email Domain', placeholder: 'e.g., prosci.com' },
    { key: 'affiliate_id', type: 'affiliate-select', label: 'Affiliate', placeholder: 'All Affiliates', showFor: ['super', 'admin', 'affiliate_admin', 'data'] },
    { key: 'license_admin_id', type: 'organization-select', label: 'License Holder', placeholder: 'All License Holders', showFor: ['super', 'admin', 'affiliate_admin', 'data'] },
    { key: 'from_date', type: 'date', label: 'From Date', placeholder: 'Select date' },
    { key: 'to_date', type: 'date', label: 'To Date', placeholder: 'Select date' },
  ],
  hasFiltersCheck: (filters: ReportFilters) =>
    Boolean(
      filters.email_domain ||
      filters.from_date ||
      filters.to_date ||
      filters.affiliate_id ||
      filters.license_admin_id
    ),
};

export function LoginCountReport() {
  return (
    <ReportPageLayout<LoginEvent>
      title="Login Count Report"
      description="Track user login activity across your organization."
      breadcrumbLabel="Login Count"
      itemLabel="login events"
      queryKey="login-count"
      fetchData={reportsService.getLoginCount}
      downloadData={reportsService.downloadLoginCount}
      filterConfig={filterConfig}
      columns={columns}
      sortData={(data) =>
        [...data].sort(
          (a, b) =>
            new Date(b.attributes.login_date).getTime() -
            new Date(a.attributes.login_date).getTime()
        )
      }
      getRowKey={(item) => item.id}
      emptyIcon={<UserRoundX />}
      emptyTitle="No login events found"
      emptyDescription="Try adjusting your filters to find login events."
    />
  );
}
